import LabelTableCell from 'Components/LabelTableCell'
import StyledTableHead from 'Components/StyledTableHead'
import StyledTableRow from 'Components/StyledTableRow'
import { type Tariff, TariffStatus } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck'
import {
  type ChipProps,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip
} from '@mui/material'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import { useState } from 'react'
import { t } from 'ttag'

function TariffDetails({ tariff }: Readonly<{ tariff: Tariff }>) {
  const chipPropsMapping: Record<TariffStatus, Pick<ChipProps, 'color' | 'label'>> = {
    [TariffStatus.ACTIVE]: { color: 'success', label: t`Active` },
    [TariffStatus.PASSIVE]: { color: 'default', label: t`Passive` },
    [TariffStatus.DRAFT]: { color: 'default', label: t`Draft` },
    [TariffStatus.STAGED]: { color: 'warning', label: t`Production ready` },
    [TariffStatus.ACTIVE_PRE_ORDER]: { color: 'default', label: t`Pre-sale` }
  }

  function getChipPropsByStatus(status: TariffStatus) {
    return chipPropsMapping[status] || { color: 'default', label: t`Unknown` }
  }

  const [copied, setCopied] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(tariff.id)
    setCopied(true)
    setTooltipOpen(true)
    setTimeout(() => {
      setCopied(false)
      setTooltipOpen(false)
    }, 2000)
  }

  const { data: singleVatDefinition = null, isFetching: isSingleVatDefinitionFetching } =
    useGetSingleVatDefinitionQuery(tariff?.vat_definition_id ?? '', {
      refetchOnMountOrArgChange: true,
      skip: !tariff?.vat_definition_id
    })

  return (
    <TableContainer component={Paper}>
      <Table sx={{ td: { border: 'none' } }} aria-label={t`Tariff details table`}>
        <StyledTableHead>
          <TableRow>
            <TableCell colSpan={2} sx={{ color: 'white' }}>
              {tariff.description}
            </TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          <StyledTableRow>
            <LabelTableCell>{t`Tariff ID`}</LabelTableCell>
            <TableCell>
              {showValueOrHyphen(tariff.id)}
              <Tooltip
                open={tooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                title={t`Copied`}
                slotProps={{
                  popper: {
                    disablePortal: true
                  }
                }}
                placement="right-end"
              >
                <IconButton onClick={handleCopy} size="small" sx={{ ml: 2 }}>
                  {copied ? <LibraryAddCheckIcon /> : <ContentCopyRoundedIcon />}
                </IconButton>
              </Tooltip>
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Tariff code`}</LabelTableCell>
            <TableCell>{showValueOrHyphen(tariff.tariff_code)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Tariffs description`}</LabelTableCell>
            <TableCell>{showValueOrHyphen(tariff.description)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Active from`}</LabelTableCell>
            <TableCell>{datetimeFormatter(tariff.active_period.valid_from)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Active to`}</LabelTableCell>
            <TableCell>{datetimeFormatter(tariff.active_period.valid_until)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`State`}</LabelTableCell>
            <TableCell>
              <Chip size="small" {...getChipPropsByStatus(tariff.status)} />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Pre-order start time`}</LabelTableCell>
            <TableCell>{datetimeFormatter(tariff.pre_order_start_timestamp)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`VAT definition`}</LabelTableCell>
            <TableCell>
              {isSingleVatDefinitionFetching ? (
                <CircularProgress size={18} />
              ) : (
                showValueOrHyphen(singleVatDefinition?.title)
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Authority`}</LabelTableCell>
            <TableCell>{tariff.pta}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Created`}</LabelTableCell>
            <TableCell>{datetimeFormatter(tariff.created_timestamp)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <LabelTableCell>{t`Modified`}</LabelTableCell>
            <TableCell>{datetimeFormatter(tariff.modified_timestamp)}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default TariffDetails
